import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlay, FiAlertTriangle, FiExternalLink, FiMaximize2, FiX, FiYoutube } = FiIcons;

// ── YouTube URL → embed ID ────────────────────────────────────────────────────
const parseYouTubeId = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    // Standard: youtube.com/watch?v=ID
    if (u.hostname.includes('youtube.com') && u.searchParams.get('v')) {
      return u.searchParams.get('v');
    }
    // Short: youtu.be/ID
    if (u.hostname === 'youtu.be') {
      return u.pathname.slice(1).split('?')[0];
    }
    // Embed: youtube.com/embed/ID
    if (u.hostname.includes('youtube.com') && u.pathname.startsWith('/embed/')) {
      return u.pathname.split('/embed/')[1].split('?')[0];
    }
    // Live: youtube.com/live/ID
    if (u.hostname.includes('youtube.com') && u.pathname.startsWith('/live/')) {
      return u.pathname.split('/live/')[1].split('?')[0];
    }
    // Shorts: youtube.com/shorts/ID
    if (u.hostname.includes('youtube.com') && u.pathname.startsWith('/shorts/')) {
      return u.pathname.split('/shorts/')[1].split('?')[0];
    }
  } catch {
    // Raw ID passed directly (11-char alphanumeric)
    if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) return url.trim();
  }
  return null;
};

// ── Aspect ratio helpers ──────────────────────────────────────────────────────
const ASPECT_RATIOS = {
  '16:9': 'aspect-video',
  '4:3':  'aspect-[4/3]',
  '1:1':  'aspect-square',
  '9:16': 'aspect-[9/16]',
};

// ── Fullscreen Lightbox ───────────────────────────────────────────────────────
const VideoLightbox = ({ videoId, title, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative w-full max-w-5xl"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all flex items-center gap-2 text-xs font-black uppercase tracking-widest"
        >
          <SafeIcon icon={FiX} className="w-4 h-4" /> Close
        </button>
        {title && (
          <p className="text-white/70 text-sm font-light mb-4 text-center truncate px-4">{title}</p>
        )}
        <div className="aspect-video w-full rounded-2xl overflow-hidden shadow-2xl bg-black">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
            title={title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full border-0"
          />
        </div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

// ── Custom Thumbnail Overlay ──────────────────────────────────────────────────
const VideoThumbnail = ({ videoId, title, onPlay }) => (
  <div
    className="relative w-full h-full cursor-pointer group"
    onClick={onPlay}
    role="button"
    tabIndex={0}
    onKeyDown={e => e.key === 'Enter' && onPlay()}
    aria-label={`Play ${title || 'video'}`}
  >
    <img
      src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
      onError={e => { e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }}
      alt={title || 'Video thumbnail'}
      className="w-full h-full object-cover"
    />
    {/* Dark overlay */}
    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
    {/* Play button */}
    <div className="absolute inset-0 flex items-center justify-center">
      <motion.div
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="w-16 h-16 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center shadow-2xl transition-colors"
      >
        <SafeIcon icon={FiPlay} className="w-7 h-7 text-white ml-1" />
      </motion.div>
    </div>
    {/* Title overlay */}
    {title && (
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <p className="text-white text-sm font-medium line-clamp-2">{title}</p>
      </div>
    )}
    {/* YouTube badge */}
    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/70 text-white px-2.5 py-1 rounded-lg">
      <SafeIcon icon={FiYoutube} className="w-3.5 h-3.5 text-red-500" />
      <span className="text-[9px] font-black uppercase tracking-widest">YouTube</span>
    </div>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
/**
 * EmbeddableVideo — Reusable YouTube video embed component.
 *
 * Props:
 *   url          {string}  YouTube URL (any format) or raw video ID. Required.
 *   title        {string}  Accessible title / caption shown below the player.
 *   description  {string}  Optional short description shown below the title.
 *   aspectRatio  {string}  '16:9' | '4:3' | '1:1' | '9:16'. Default: '16:9'.
 *   autoplay     {boolean} Autoplay on embed load (not on thumbnail click). Default: false.
 *   showControls {boolean} Show YouTube player controls. Default: true.
 *   allowFullscreen {boolean} Show fullscreen button. Default: true.
 *   useThumbnail {boolean} Show custom thumbnail before user clicks play. Default: true.
 *   lightbox     {boolean} Open in fullscreen lightbox on play. Default: false.
 *   className    {string}  Additional wrapper class.
 *   caption      {string}  Optional italic caption shown under the player.
 *   compact      {boolean} Minimal styling — no card chrome. Default: false.
 */
const EmbeddableVideo = ({
  url,
  title,
  description,
  aspectRatio    = '16:9',
  autoplay       = false,
  showControls   = true,
  allowFullscreen = true,
  useThumbnail   = true,
  lightbox       = false,
  className      = '',
  caption,
  compact        = false,
}) => {
  const [playing,      setPlaying]      = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const videoId = useMemo(() => parseYouTubeId(url), [url]);

  const embedSrc = useMemo(() => {
    if (!videoId) return null;
    const params = new URLSearchParams({
      rel:           '0',
      modestbranding: '1',
      ...(autoplay || playing ? { autoplay: '1' } : {}),
      ...(showControls ? {} : { controls: '0' }),
    });
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
  }, [videoId, autoplay, playing, showControls]);

  const handlePlay = useCallback(() => {
    if (lightbox) {
      setLightboxOpen(true);
    } else {
      setPlaying(true);
    }
  }, [lightbox]);

  // ── Invalid / missing URL ─────────────────────────────────────────────────
  if (!videoId) {
    return (
      <div className={`rounded-2xl border-2 border-dashed border-red-200 bg-red-50 p-6 flex items-start gap-3 ${className}`}>
        <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-red-700">Invalid YouTube URL</p>
          <p className="text-xs text-red-500 font-light mt-0.5 font-mono break-all">{url || '(no URL provided)'}</p>
          <p className="text-xs text-red-400 font-light mt-2">
            Supported formats: <code className="bg-red-100 px-1 rounded">youtube.com/watch?v=…</code>{' '}
            <code className="bg-red-100 px-1 rounded">youtu.be/…</code>{' '}
            <code className="bg-red-100 px-1 rounded">youtube.com/embed/…</code>
          </p>
        </div>
      </div>
    );
  }

  const ratioClass = ASPECT_RATIOS[aspectRatio] || ASPECT_RATIOS['16:9'];

  // ── Compact mode ──────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div className={`rounded-2xl overflow-hidden ${className}`}>
        <div className={`relative w-full ${ratioClass} bg-black`}>
          {useThumbnail && !playing ? (
            <VideoThumbnail videoId={videoId} title={title} onPlay={handlePlay} />
          ) : (
            <iframe
              src={embedSrc}
              title={title || 'Embedded video'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen={allowFullscreen}
              className="absolute inset-0 w-full h-full border-0"
            />
          )}
        </div>
        {caption && <p className="text-xs text-text-muted italic font-light mt-2 px-1">{caption}</p>}
        {lightboxOpen && <VideoLightbox videoId={videoId} title={title} onClose={() => setLightboxOpen(false)} />}
      </div>
    );
  }

  // ── Full card mode ────────────────────────────────────────────────────────
  return (
    <div className={`bg-white rounded-[28px] border border-brand-dark overflow-hidden shadow-sm ${className}`}>
      {/* Player area */}
      <div className={`relative w-full ${ratioClass} bg-black`}>
        {useThumbnail && !playing ? (
          <VideoThumbnail videoId={videoId} title={title} onPlay={handlePlay} />
        ) : (
          <iframe
            src={embedSrc}
            title={title || 'Embedded video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen={allowFullscreen}
            className="absolute inset-0 w-full h-full border-0"
          />
        )}
      </div>

      {/* Footer */}
      {(title || description || caption) && (
        <div className="p-5 space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {title && (
                <h4 className="font-serif text-accent-navy text-base leading-snug">{title}</h4>
              )}
              {description && (
                <p className="text-xs text-text-muted font-light leading-relaxed mt-1">{description}</p>
              )}
              {caption && (
                <p className="text-[10px] text-text-muted italic font-light mt-2">{caption}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {lightbox && (
                <button
                  onClick={() => setLightboxOpen(true)}
                  className="p-2 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:text-accent-gold hover:border-accent-gold transition-all"
                  title="Open in fullscreen"
                >
                  <SafeIcon icon={FiMaximize2} className="w-3.5 h-3.5" />
                </button>
              )}
              <a
                href={`https://www.youtube.com/watch?v=${videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:text-accent-gold hover:border-accent-gold transition-all"
                title="Open on YouTube"
              >
                <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {lightboxOpen && (
        <VideoLightbox videoId={videoId} title={title} onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
};

export default EmbeddableVideo;